import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getPreconsentDataFromStorage, initialiseConsent } from '../actions';
import CancelLogin from './CancelLogin';
import { postMessageSuccess } from '../helpers/postMessage';

const MAX_MOBILE_ATTEMPTS = 2;
const MOBILE_DISABLED_MESSAGE = 'Disabled due to failed attempts';

const ConsentDecoupledInitialised = (props) => {
  const dispatch = useDispatch();
  const [activeOption, setActiveOption] = useState(0); // First option active by default
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isPhoneMasked, setIsPhoneMasked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  // Set the query-string phone number, or the pn_model_bank default
  useEffect(() => {
    const { rawPhone } = getPreconsentDataFromStorage();
    const predefinedPhone = props.predefinedPhone || rawPhone;

    if (props.attemptedPhone) {
      setPhoneNumber(props.attemptedPhone);
      setIsPhoneMasked(false);
    } else if (predefinedPhone) {
      setPhoneNumber(predefinedPhone);
      setIsPhoneMasked(true);
    } else if (props.institution?.slug === 'pn_model_bank') {
      setPhoneNumber('+64-422481637');
      setIsPhoneMasked(true);
    }
  }, [props.attemptedPhone, props.institution?.slug, props.predefinedPhone]);

  // Handle error message from backend response
  useEffect(() => {
    if (props.phone_error && props.error_message) {
      setPhoneError(props.error_message);
    }
  }, [props.phone_error, props.error_message]);

  // Phone number validation function
  const validatePhoneNumber = (phone) => {
    // Remove spaces, dashes, parentheses, but keep + and digits
    const cleaned = phone.replace(/[\s\-()]/g, '');
    
    // Extract digits only (excluding + for length calculation)
    const digitsOnly = cleaned.replace(/\+/g, '');
    
    // Check if it contains only valid characters (digits, +, spaces, dashes, parentheses)
    const validCharPattern = /^[\d+\s\-()]+$/
    if (!validCharPattern.test(phone)) {
      return 'Phone number contains invalid characters';
    }
    
    // Check digit length (minimum 7, maximum 12 digits)
    if (digitsOnly.length < 7) {
      return 'Phone number must have at least 7 digits';
    } else if (digitsOnly.length > 12) {
      return 'Phone number cannot exceed 12 digits';
    }
    
    // Check if + appears only at the beginning
    const plusCount = (cleaned.match(/\+/g) || []).length;
    if (plusCount > 1) {
      return 'Only one + allowed at the beginning';
    }
    
    if (cleaned.includes('+') && !cleaned.startsWith('+')) {
      return 'The + must be at the beginning of the phone number';
    }
    
    return null; // Valid
  };

  // Sanitize phone number input
  const sanitizePhoneNumber = (phone) => {
    // Allow digits, +, spaces, dashes, and parentheses only
    return phone.replace(/[^\d+\s\-()]/g, '');
  };

  const maskPhoneNumber = (phone) => {
    const normalizedPhone = phone.replace(/[\s\-()]/g, '');
    if (!normalizedPhone) return '';

    const prefixLength = Math.min(3, normalizedPhone.length);
    const remainingLength = normalizedPhone.length - prefixLength;
    const suffixLength = remainingLength > 0 ? Math.min(4, remainingLength) : 0;
    const prefix = normalizedPhone.slice(0, prefixLength);

    if (suffixLength === 0) return prefix;

    return `${prefix}***${normalizedPhone.slice(-suffixLength)}`;
  };

  const revealPhoneNumber = () => {
    if (isPhoneMasked) {
      setIsPhoneMasked(false);
    }
  };

  // Handle phone number input change
  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    setIsPhoneMasked(false);
    
    // Check for invalid characters first
    const validCharPattern = /^[\d+\s\-()]*$/;
    if (value && !validCharPattern.test(value)) {
      setPhoneError('Enter valid phone number (digits, spaces, dashes, parentheses, and + only)');
      // Still sanitize and set the phone number
      const sanitized = sanitizePhoneNumber(value);
      setPhoneNumber(sanitized);
      return;
    }
    
    const sanitized = sanitizePhoneNumber(value);
    setPhoneNumber(sanitized);
    
    // Check for double plus immediately
    const plusCount = (sanitized.match(/\+/g) || []).length;
    if (plusCount > 1) {
      setPhoneError('Only one + allowed at the beginning');
      return;
    }
    
    if (sanitized.trim()) {
      const validationError = validatePhoneNumber(sanitized);
      setPhoneError(validationError || '');
    } else {
      // Clear error when field is empty (user is still typing)
      setPhoneError('');
    }
  };

  // Parse the decoupled_flow JSON from props
  const parseDecoupledFlow = () => {
    try {
      if (props.decoupled_flow && typeof props.decoupled_flow === 'string') {
        return JSON.parse(props.decoupled_flow);
      } else if (props.decoupled_flow && typeof props.decoupled_flow === 'object') {
        return props.decoupled_flow;
      }
      return null;
    } catch (error) {
      console.error('Error parsing decoupled flow:', error);
      return null;
    }
  };

  const decoupledFlowData = parseDecoupledFlow();
  const verificationOptions = decoupledFlowData?.verificationOptions;
  const optionKeys = Object.keys(verificationOptions?.options || {});
  const optionKeysSignature = optionKeys.join(',');
  const isMobileDisabled = (props.phone_failed_attempts || 0) >= MAX_MOBILE_ATTEMPTS;

  // After too many failed mobile attempts, force the user onto the web option
  useEffect(() => {
    if (!isMobileDisabled) return;

    const keys = optionKeysSignature ? optionKeysSignature.split(',') : [];
    const mobileIndex = keys.indexOf('mobile');
    const webIndex = keys.indexOf('web');

    if (webIndex !== -1 && activeOption === mobileIndex) {
      setActiveOption(webIndex);
    }
  }, [isMobileDisabled, optionKeysSignature, activeOption]);

  if (!verificationOptions) {
    return (
      <div id="intro">
        <div className="consent-data-wrapper">
          <div className="formMain">
            <h2 className="sub-heading">Loading verification options...</h2>
          </div>
        </div>
      </div>
    );
  }

  const { heading, subtitle, options, institutions } = verificationOptions;
  const institutionName = props.institution?.name || 'your bank';
  const formattedSubtitle = subtitle?.replace('{INSTITUTION}', institutionName) || '';

  const toggleAccordion = (optionIndex, optionKey) => {
    if (optionKey === 'mobile' && isMobileDisabled) return;
    setActiveOption(optionIndex);
  };

  const onCancel = () => {
    const slug = props.institution?.slug;
    props.dispatch(postMessageSuccess("decoupled_flow_cancelled", {data: slug}, "cancelled"));
    props.dispatch({ type: 'LOGIN_PROCESS_CANCELLED' });
  };

  const handleSubmit = async (event, optionKey, optionIndex) => {
    event.preventDefault();
    
    if (isSubmitting) return;
    
    const option = options[optionKey];
    const verificationMethod = optionKey;
    
    // Validate phone number for mobile verification
    if (optionKey === 'mobile') {
      if (isMobileDisabled) {
        setPhoneError(MOBILE_DISABLED_MESSAGE);
        return;
      }

      if (!phoneNumber.trim()) {
        setPhoneError('Please enter your mobile number');
        return;
      }
      
      const validationError = validatePhoneNumber(phoneNumber);
      if (validationError) {
        setPhoneError(validationError);
        return;
      }
    }
    
    setIsSubmitting(true);
    
    try {
      const formData = {
        verification_method: verificationMethod,
        consent_guid: props.consent_guid,
      };
      
      // Add user_token if available (from props or sessionData)
      if (props.user_token) {
        formData.user_token = props.user_token;
      }
      
      if (optionKey === 'mobile') {
        // For pn_model_bank, send phone number as entered (preserve formatting)
        // For other institutions, clean phone number (remove spaces, dashes, parentheses)
        if (props.institution?.slug === 'pn_model_bank') {
          formData.phone_number = phoneNumber.trim();
        } else {
          formData.phone_number = phoneNumber;
        }
      }
      
      // Track GA event if available
      if (option.gaEvent && window.gtag) {
        window.gtag('event', 'click', {
          event_category: 'consent',
          event_label: option.gaEvent
        });
      }
      
      // Send postMessage based on option selected
      const slug = props.institution?.slug;
      if (verificationMethod === 'mobile') {
        dispatch(postMessageSuccess("option_selected_mobile", {data: slug}));
      } else if (verificationMethod === 'web') {
        dispatch(postMessageSuccess("option_selected_web", {data: slug}));
      }
      
      // Dispatch the initialise consent action with form data
      
      await dispatch(initialiseConsent(props.institution?.slug, formData));
      
    } catch (error) {
      console.error('Error submitting consent:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div id="intro">
        <div className="consent-data-wrapper">
          <div className="formMain">
            <h2 className="sub-heading">{heading}</h2>
            <p className="consent-verify-subtitle">{formattedSubtitle}</p>

            {isMobileDisabled && (
              <div className="consent-verify-disabled-notice" role="alert">
                {"The mobile number you entered could not be verified by the bank, please use the alternate website redirect flow as shown below." || MOBILE_DISABLED_MESSAGE}
              </div>
            )}

            {optionKeys.map((key, index) => {
              const option = options[key];
              const optionIndex = index + 1;
              const accordionId = `option${optionIndex}`;
              const isDisabledOption = key === 'mobile' && isMobileDisabled;
              const isActive = activeOption === index && !isDisabledOption;
              const institutionSteps = institutions?.[props?.institution?.slug]?.[key]?.steps || options?.[key]?.steps || [];
              const inputLabel = institutions?.[props?.institution?.slug]?.[key]?.inputLabel || options?.[key]?.inputLabel || '';

              return (
                <div
                  key={key}
                  className={`consent-verify-option${isActive ? ' active' : ''}${isDisabledOption ? ' disabled' : ''}`}
                  data-accordion={accordionId}
                >
                  <div
                    className="consent-verify-option-header"
                    onClick={() => toggleAccordion(index, key)}
                  >
                    <h3>
                      Option {optionIndex} — {option.title}
                      {!isDisabledOption && option.badge && (
                        <span className="consent-verify-badge">{option.badge}</span>
                      )}
                      {!isDisabledOption && (
                        <span
                          className="consent-verify-method-tag"
                          dangerouslySetInnerHTML={{ __html: option.methodTag }}
                        />
                      )}
                      {isDisabledOption && (
                        <span className="consent-verify-disabled-tag">{MOBILE_DISABLED_MESSAGE}</span>
                      )}
                      <span className="consent-verify-toggle">
                        {isActive ? '▼' : '▶'}
                      </span>
                    </h3>
                  </div>
                  <div className="consent-verify-option-body">
                    <div className="consent-verify-option-inner">
                      {institutionSteps.length > 0 && (
                        <ul className="consent-verify-steps">
                          {institutionSteps.map((step, stepIndex) => (
                            <li
                              key={stepIndex}
                              dangerouslySetInnerHTML={{ __html: step }}
                            />
                          ))}
                        </ul>
                      )}


                      <form
                        onSubmit={(e) => handleSubmit(e, key, index)}
                        className="initialise_consent"
                        autoComplete="off"
                      >
                        {inputLabel && (
                          <div className="consent-verify-input-group">
                            <label htmlFor={`phone_number_${key}`}>
                              <span>{inputLabel}</span>
                            </label>
                            <input
                              type="tel"
                              name="phone_number"
                              id={`phone_number_${key}`}
                              placeholder=''
                              autoComplete="tel"
                              required
                              maxLength="25"
                              disabled={isDisabledOption}
                              value={isPhoneMasked ? maskPhoneNumber(phoneNumber) : phoneNumber}
                              onFocus={revealPhoneNumber}
                              onClick={revealPhoneNumber}
                              onChange={handlePhoneNumberChange}
                              data-masked-phone={isPhoneMasked}
                              className={phoneError && !isDisabledOption ? 'error' : ''}
                            />
                            {isDisabledOption ? (
                              <div className="error-message">{MOBILE_DISABLED_MESSAGE}</div>
                            ) : phoneError && (
                              <div className="error-message">{phoneError}</div>
                            )}
                          </div>
                        )}

                        <div className="formSecondary">
                          <button
                            type="submit"
                            title={isDisabledOption ? MOBILE_DISABLED_MESSAGE : option.buttonLabel}
                            className="form-control btn btn-primary"
                            data-gaevent={option.gaEvent || ''}
                            disabled={isDisabledOption || isSubmitting}
                          >
                            {isSubmitting ? 'Processing...' : option.buttonLabel}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <CancelLogin
            action={onCancel}
            theme={props.theme}
            backToSelectionBtnText={props.theme?.text?.backToSelectionBtn}
            requireWinz={false}
          />
        </div>
      </div>

      <style>{`
        .consent-verify-subtitle {
          margin-bottom: 20px;
        }
        .consent-verify-option {
          background: rgba(255, 255, 255, 0.5);
          border: 1px solid #cccccc;
          border-radius: 6px;
          margin-bottom: 16px;
        }
        .consent-verify-option-header {
          background-color: rgba(195, 195, 195, 0.3);
          padding: 12px 16px 8px;
          cursor: pointer;
          user-select: none;
        }
        .consent-verify-option-header:hover {
          background-color: rgba(195, 195, 195, 0.5);
        }
        .consent-verify-option-header h3 {
          margin: 0 0 4px 0;
          font-size: 16px;
        }
        .consent-verify-toggle {
          float: right;
          margin-top: 2px;
        }
        .consent-verify-badge {
          background-color: #28a745;
          color: white;
          font-size: 12px;
          padding: 2px 6px;
          border-radius: 3px;
          margin-left: 8px;
        }
        .consent-verify-option-body {
          display: block;
          max-height: 1000px;
          overflow: hidden;
          transition: max-height 0.3s ease;
        }
        .consent-verify-option:not(.active) .consent-verify-option-body {
          display: none;
        }
        .consent-verify-option-inner {
          padding: 16px;
        }
        .consent-verify-steps {
          margin-bottom: 16px;
          padding-left: 20px;
        }
        .consent-verify-input-group {
          margin-bottom: 16px;
        }
        .consent-verify-input-group label {
          display: block;
          margin-bottom: 4px;
          font-weight: 500;
        }
        .consent-verify-input-group input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 14px;
        }
        .consent-verify-input-group input.error {
          border-color: #dc3545;
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
        }
        .error-message {
          color: #dc3545;
          font-size: 12px;
          margin-top: 4px;
        }
        .consent-verify-method-tag {
          font-size: 12px;
          color: #666;
          display: block;
        }
          float: right;
          font-size: 14px;
          transition: transform 0.3s ease;
        }
        .consent-verify-option.active .consent-verify-toggle {
          transform: rotate(0deg);
        }
        .consent-verify-badge {
          display: inline-block;
          background: #28a745;
          color: #fff;
          font-size: 11px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 10px;
          vertical-align: middle;
          margin-left: 6px;
        }
        .consent-verify-method-tag {
          display: inline-block;
          background: #e9ecef;
          color: #555;
          font-size: 11px;
          font-weight: 500;
          padding: 2px 8px;
          border-radius: 10px;
          vertical-align: middle;
          margin-left: 4px;
        }
        .consent-verify-option-body {
          display: grid;
          grid-template-rows: 1fr;
          transition: grid-template-rows 0.35s ease;
        }
        .consent-verify-option:not(.active) .consent-verify-option-body {
          grid-template-rows: 0fr;
        }
        .consent-verify-option-body > .consent-verify-option-inner {
          overflow: hidden;
          padding: 12px 16px 16px;
          transition: padding 0.35s ease, opacity 0.3s ease;
          opacity: 1;
        }
        .consent-verify-option:not(.active) .consent-verify-option-body > .consent-verify-option-inner {
          padding-top: 0;
          padding-bottom: 0;
          opacity: 0;
        }
        .consent-verify-steps {
          margin: 0 0 16px 0;
          padding-left: 20px;
        }
        .consent-verify-steps li {
          margin-bottom: 6px;
          font-size: 14px;
          list-style: disc inside;
        }
        .consent-verify-input-group {
          margin-bottom: 16px;
        }
        .consent-verify-input-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 600;
          font-size: 14px;
        }
        .consent-verify-input-group input[type="tel"] {
          width: 100%;
          max-width: 300px;
          padding: 8px 12px;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 14px;
          box-sizing: border-box;
        }
        .submit.mainButton:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .consent-verify-disabled-notice {
          background: #f8d7da;
          border: 1px solid #f5c2c7;
          color: #842029;
          border-radius: 4px;
          padding: 10px 12px;
          margin-bottom: 16px;
          font-size: 14px;
        }
        .consent-verify-option.disabled {
          opacity: 0.6;
        }
        .consent-verify-option.disabled .consent-verify-option-header {
          cursor: not-allowed;
        }
        .consent-verify-option.disabled .consent-verify-option-header:hover {
          background-color: rgba(195, 195, 195, 0.3);
        }
        .consent-verify-disabled-tag {
          display: inline-block;
          background: #dc3545;
          color: #fff;
          font-size: 11px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 10px;
          vertical-align: middle;
          margin-left: 6px;
        }
        .consent-verify-input-group input[type="tel"]:disabled {
          background: #e9ecef;
          cursor: not-allowed;
        }
        .formSecondary .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </>
  );
};

export default ConsentDecoupledInitialised;